import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { CharterDAO } from '../../../../../core/services/dao/charterDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { Router } from '@angular/router';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs/Rx';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { DialogsService } from '../../../../../core/services/dialog.service';
import * as moment from 'moment';
import { CaptainSearchParams } from './captain-search/captain-search.component';
import {
  getFadeInTransition,
  getFadeOutTransition,
} from '../../../../../core/pure-functions/animations/ng-animations-provider';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-charter-captains',
  templateUrl: './charter-captains.component.html',
  styleUrls: ['./charter-captains.component.scss'],
  animations: [getFadeInTransition(1000)],
})
export class CharterCaptainsComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  charters: Charter[] = [];
  subs: Subscription[] = [];
  stateWaterBodyMappings = [];
  loading = false;

  currentPageIndex = 0;

  pageNumberIndexesToDisplay: number[] = [];
  totalPages: number;
  maxPageNumberIndex: number;
  isMobile: boolean;
  userSearched = false;
  searchParams: CaptainSearchParams = {
    charterId: null,
    date: null,
    waterBodyIds: [],
    limit: 5,
    page: 0,
  };
  isBrowser: boolean;

  constructor(
    private charterDao: CharterDAO,
    private router: Router,
    private dialogService: DialogsService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.isMobile = window && window.screen && window.screen.width < 960;
    this.checkForFilters();
    this.initAsyncData();
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  navToAssignCaptain(charterId: number) {
    this.router.navigate(['manage/assign-captain/' + charterId]);
  }

  loadCharterUsingSearchParams(searchParams: CaptainSearchParams) {
    this.loading = true;
    let charterSub = this.charterDao
      .getOpenChartersWithoutAssignedCaptains(searchParams)
      .subscribe((chartersWithRowCount) => {
        this.charters = chartersWithRowCount.rows;
        this.sortChartersByDate();
        this.initPageNumbers(chartersWithRowCount);
        this.mapCharterMetaData();
        this.loading = false;
      });
    this.subs.push(charterSub);
  }

  refreshPageData(): void {
    this.currentPageIndex = 0;
    this.charters = [];
    this.searchParams = {
      charterId: null,
      date: null,
      waterBodyIds: [],
      limit: 5,
      page: 0,
    };
    this.setSearchParamsInLocalStorage();
    this.initAsyncData();
  }

  initPageNumbers(chartersWithRowCount) {
    this.totalPages = Math.ceil(chartersWithRowCount.count / 5);
    this.pageNumberIndexesToDisplay = [];
    for (let pageNumber = 0; pageNumber < this.totalPages; pageNumber++) {
      this.pageNumberIndexesToDisplay.push(pageNumber);
    }
    this.maxPageNumberIndex =
      this.pageNumberIndexesToDisplay[
        this.pageNumberIndexesToDisplay.length - 1
      ];
  }

  mapCharterMetaData() {
    this.charters.forEach((charter) => {
      let responseTotals = {
        totalCaptains: 0,
        available: 0,
        unavailable: 0,
        noResponse: 0,
      };

      responseTotals.totalCaptains = charter.boat.vesselCaptainMap.length;
      charter.captainGigs.forEach((response) => {
        if (response.responseText === 'Available') {
          responseTotals.available++;
        } else if (response.responseText === 'Unavailable') {
          responseTotals.unavailable++;
        } else if (
          response.responseText === 'No Response' ||
          !response.responseText
        ) {
          responseTotals.noResponse++;
        }
      });

      // add captains without gigs to the totals in the "no response" bucket
      let totalCaptsWithGigs =
        responseTotals.noResponse +
        responseTotals.unavailable +
        responseTotals.available;
      let totalCaptsWithoutGigs =
        responseTotals.totalCaptains - totalCaptsWithGigs;
      if (totalCaptsWithoutGigs > 0) {
        responseTotals.noResponse += totalCaptsWithoutGigs;
      }
      // @ts-ignore
      charter.responseTotals = responseTotals;
      // @ts-ignore
      charter.hoursUntilStart = this.calculateHoursUntilCharterStart(charter);
      // @ts-ignore
      charter.formattedDate = this.getFormattedCharterDate(charter);
    });
  }

  calculateHoursUntilCharterStart(charter: Charter): number {
    // get hours till charter start time, if < 48 hrs then charter is shotgun
    let currentTime = moment();
    let charterStart = moment(
      charter.date + ' ' + charter.startTime,
      CharterDateTimeFormat,
    );

    let diff = moment.duration(charterStart.diff(currentTime));

    return parseInt(String(diff.asHours()));
  }

  initStateWaterBodyMappings() {
    const waterBodySub = this.charterDao
      .getAllOpenChartersWithWaterBodiesWithoutAssignedCaptains()
      .subscribe((chartersWithWaterBodies) => {
        chartersWithWaterBodies.forEach((charter) => {
          if (charter.waterBody) {
            let stateExistsInArray = this.stateWaterBodyMappings.find(
              (waterbody) => waterbody.state === charter.waterBody.displayState,
            );

            if (!stateExistsInArray) {
              this.stateWaterBodyMappings.push({
                waterBodies: [
                  {
                    name: charter.waterBody.displayName,
                    id: charter.waterBody.id,
                  },
                ],
                state: charter.waterBody.displayState,
              });
            } else {
              let waterBodyExistsInArray = stateExistsInArray.waterBodies.find(
                (waterbody) => waterbody.name === charter.waterBody.displayName,
              );
              if (!waterBodyExistsInArray) {
                stateExistsInArray.waterBodies.push({
                  name: charter.waterBody.displayName,
                  id: charter.waterBody.id,
                });
              }
            }
          }
        });
      });
    this.subs.push(waterBodySub);
  }

  openSearchModal() {
    const dialogSub = this.dialogService
      .headCaptainSerachDialog(this.stateWaterBodyMappings, this.searchParams)
      .subscribe((dialogResult) => {
        if (dialogResult) {
          this.userSearched = true;
          this.charters = dialogResult.charters.rows;
          this.sortChartersByDate();
          this.searchParams = dialogResult.searchParams;
          this.currentPageIndex = 0;
          this.initPageNumbers(dialogResult.charters);
          this.mapCharterMetaData();
          this.setSearchParamsInLocalStorage();
        }
      });
    this.subs.push(dialogSub);
  }

  private getFormattedCharterDate(charter: Charter) {
    return moment(
      charter.date + ' ' + charter.startTime,
      CharterDateTimeFormat,
    ).format('MMMM D YYYY, h:mm A');
  }

  pageClick(pageNumber: number) {
    this.currentPageIndex = pageNumber;
    this.searchParams.page = this.currentPageIndex;
    this.searchParams.limit = 5;

    this.loadCharterUsingSearchParams(this.searchParams);
    document.querySelector('.mat-sidenav-content').scrollTop = 0;
  }

  private initAsyncData(): void {
    this.initStateWaterBodyMappings();
    this.loadCharterUsingSearchParams(this.searchParams);
  }

  private sortChartersByDate(): void {
    this.charters = this.charters.sort((a, b) => {
      const aFull = a.date + ' ' + a.startTime;
      const bFull = b.date + ' ' + b.startTime;
      const aUnixTime = moment(aFull, CharterDateTimeFormat).unix();
      const bUnixTime = moment(bFull, CharterDateTimeFormat).unix();
      if (aUnixTime > bUnixTime) {
        return 1;
      } else if (aUnixTime < bUnixTime) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  private checkForFilters() {
    if (this.isBrowser) {
      if (localStorage.getItem('headCaptainWaterBodySearchParams')) {
        this.searchParams.waterBodyIds = JSON.parse(
          localStorage.getItem('headCaptainWaterBodySearchParams'),
        );
      }
    }
  }

  private setSearchParamsInLocalStorage() {
    if (this.isBrowser) {
      localStorage.setItem(
        'headCaptainWaterBodySearchParams',
        JSON.stringify(this.searchParams.waterBodyIds),
      );
    }
  }
}

export const CharterDateTimeFormat = 'YYYY-MM-DD hh:mm a';
